/**
 * Composable para comparar objetos JSON y generar diffs detallados
 */
export function useJsonDiff() {
  
  /**
   * Tipos de cambios detectados
   */
  const ChangeType = {
    ADDED: 'added',
    REMOVED: 'removed',
    MODIFIED: 'modified',
    UNCHANGED: 'unchanged'
  }

  /**
   * Compara dos valores primitivos o complejos de forma profunda
   */
  function areEqual(val1, val2, propName = null) {
    // Comparación estricta para primitivos y referencias
    if (val1 === val2) return true
    
    // Si uno es null/undefined y el otro no
    if (val1 == null || val2 == null) return val1 === val2
    
    // Para propiedades de ID, normalizar a string antes de comparar
    const isIdProp = propName && ['id', 'ID', 'codigo', 'code', 'key'].includes(propName)
    if (isIdProp && val1 != null && val2 != null) {
      return String(val1) === String(val2)
    }
    
    // Si los tipos son diferentes
    if (typeof val1 !== typeof val2) return false
    
    // Comparación de objetos y arrays
    if (typeof val1 === 'object' && typeof val2 === 'object') {
      return deepEqual(val1, val2)
    }
    
    return false
  }

  /**
   * Comparación profunda de objetos y arrays
   */
  function deepEqual(obj1, obj2) {
    // Si son el mismo objeto en memoria
    if (obj1 === obj2) return true
    
    // Manejar null
    if (obj1 == null || obj2 == null) return obj1 === obj2
    
    // Manejar Date
    if (obj1 instanceof Date && obj2 instanceof Date) {
      return obj1.getTime() === obj2.getTime()
    }
    
    // Manejar RegExp
    if (obj1 instanceof RegExp && obj2 instanceof RegExp) {
      return obj1.toString() === obj2.toString()
    }
    
    // Manejar arrays
    if (Array.isArray(obj1) && Array.isArray(obj2)) {
      if (obj1.length !== obj2.length) return false
      
      for (let i = 0; i < obj1.length; i++) {
        if (!areEqual(obj1[i], obj2[i])) return false
      }
      
      return true
    }
    
    // Manejar objetos planos
    if (Array.isArray(obj1) || Array.isArray(obj2)) return false
    
    const keys1 = Object.keys(obj1)
    const keys2 = Object.keys(obj2)
    
    // Diferente número de propiedades
    if (keys1.length !== keys2.length) return false
    
    // Comparar cada propiedad recursivamente, pasando el nombre de la propiedad
    for (const key of keys1) {
      if (!keys2.includes(key)) return false
      if (!areEqual(obj1[key], obj2[key], key)) return false
    }
    
    return true
  }

  /**
   * Encuentra el ID de un elemento (busca diferentes propiedades comunes)
   */
  function getItemId(item) {
    if (!item || typeof item !== 'object') return null
    
    // Buscar propiedades comunes de identificación
    const idProps = ['id', 'ID', 'nombre', 'name', 'codigo', 'code', 'key']
    for (const prop of idProps) {
      if (item[prop] !== undefined && item[prop] !== null) {
        return String(item[prop])
      }
    }
    
    // Si no encuentra ID, usar una representación del objeto
    return JSON.stringify(item)
  }

  /**
   * Compara dos arrays de objetos y detecta cambios
   */
  function compareArrays(oldArray, newArray, itemName = 'elemento', friendlyDataType = 'elemento') {
    const changes = []
    
    if (!Array.isArray(oldArray)) oldArray = []
    if (!Array.isArray(newArray)) newArray = []

    // Crear escenarios por ID para comparación eficiente
    const oldMap = new Map()
    const newMap = new Map()
    
    oldArray.forEach((item, index) => {
      const id = getItemId(item)
      oldMap.set(id, { item, index })
    })
    
    newArray.forEach((item, index) => {
      const id = getItemId(item)
      newMap.set(id, { item, index })
    })

    // Detectar elementos eliminados
    oldMap.forEach((oldData, id) => {
      if (!newMap.has(id)) {
        changes.push({
          type: ChangeType.REMOVED,
          id,
          itemName: friendlyDataType,
          field: itemName,
          oldValue: oldData.item,
          newValue: null,
          oldIndex: oldData.index,
          description: `${itemName} eliminado: ${getItemDescription(oldData.item)}`
        })
      }
    })

    // Detectar elementos nuevos y modificados
    newMap.forEach((newData, id) => {
      const oldData = oldMap.get(id)
      
      if (!oldData) {
        // Elemento nuevo
        changes.push({
          type: ChangeType.ADDED,
          id,
          itemName: friendlyDataType,
          field: itemName,
          oldValue: null,
          newValue: newData.item,
          newIndex: newData.index,
          description: `${itemName} añadido: ${getItemDescription(newData.item)}`
        })
      } else if (!areEqual(oldData.item, newData.item)) {
        // Elemento modificado - convertir cada campo en un cambio independiente
        const fieldChanges = compareObjects(oldData.item, newData.item, `[id=${id}]`)
        
        // En lugar de agrupar los fieldChanges, crear un cambio independiente para cada campo
        fieldChanges.forEach(fieldChange => {
          changes.push({
            type: fieldChange.type,
            id,
            itemName: friendlyDataType,
            // El field ya contiene el path completo desde compareObjects
            field: fieldChange.field,
            oldValue: fieldChange.oldValue,
            newValue: fieldChange.newValue,
            typeChange: fieldChange.typeChange,
            isArray: fieldChange.isArray,
            description: `${friendlyDataType} [${getItemDescription(newData.item)}] - ${fieldChange.field}: ${getChangeDescription(fieldChange)}`
          })
        })
      }
    })

    return changes
  }
  
  /**
   * Obtiene una descripción del tipo de cambio en un campo
   */
  function getChangeDescription(fieldChange) {
    if (fieldChange.type === ChangeType.ADDED) {
      return 'campo añadido'
    } else if (fieldChange.type === ChangeType.REMOVED) {
      return 'campo eliminado'
    } else {
      return 'modificado'
    }
  }

  /**
   * Compara dos objetos campo por campo (recursivamente)
   */
  function compareObjects(oldObj, newObj, prefix = '') {
    const fieldChanges = []
    const allKeys = new Set([
      ...Object.keys(oldObj || {}),
      ...Object.keys(newObj || {})
    ])

    allKeys.forEach(key => {
      const oldVal = oldObj?.[key]
      const newVal = newObj?.[key]
      const fieldPath = prefix ? `${prefix}.${key}` : key

      // Campo añadido
      if (oldVal === undefined && newVal !== undefined) {
        fieldChanges.push({
          field: fieldPath,
          type: ChangeType.ADDED,
          oldValue: undefined,
          newValue: newVal
        })
      } 
      // Campo eliminado
      else if (oldVal !== undefined && newVal === undefined) {
        fieldChanges.push({
          field: fieldPath,
          type: ChangeType.REMOVED,
          oldValue: oldVal,
          newValue: undefined
        })
      } 
      // Ambos son arrays -> comparar elemento por elemento usando IDs si están disponibles
      else if (
        Array.isArray(oldVal) && Array.isArray(newVal) &&
        !areEqual(oldVal, newVal)
      ) {
        // Verificar si son arrays de objetos con IDs
        const hasIds = oldVal.length > 0 && newVal.length > 0 &&
                       typeof oldVal[0] === 'object' && oldVal[0] !== null &&
                       typeof newVal[0] === 'object' && newVal[0] !== null &&
                       (oldVal[0].id !== undefined || oldVal[0].ID !== undefined ||
                        newVal[0].id !== undefined || newVal[0].ID !== undefined)
        
        if (hasIds) {
          // Usar IDs para emparejar objetos
          const oldMap = new Map()
          const newMap = new Map()
          
          oldVal.forEach((item, index) => {
            const id = getItemId(item)
            oldMap.set(id, { item, index })
          })
          
          newVal.forEach((item, index) => {
            const id = getItemId(item)
            newMap.set(id, { item, index })
          })
          
          // Detectar elementos eliminados
          oldMap.forEach((oldData, id) => {
            if (!newMap.has(id)) {
              fieldChanges.push({
                field: `${fieldPath}[id=${id}]`,
                type: ChangeType.REMOVED,
                oldValue: oldData.item,
                newValue: undefined
              })
            }
          })
          
          // Detectar elementos nuevos y modificados
          newMap.forEach((newData, id) => {
            const oldData = oldMap.get(id)
            
            if (!oldData) {
              // Elemento nuevo
              fieldChanges.push({
                field: `${fieldPath}[id=${id}]`,
                type: ChangeType.ADDED,
                oldValue: undefined,
                newValue: newData.item
              })
            } else if (!areEqual(oldData.item, newData.item)) {
              // Elemento modificado - comparar campo por campo
              const nestedChanges = compareObjects(oldData.item, newData.item, `${fieldPath}[id=${id}]`)
              fieldChanges.push(...nestedChanges)
            }
          })
        } else {
          // Arrays sin IDs o arrays de primitivos - comparar por índice
          const allPrimitives = oldVal.every(v => typeof v !== 'object' || v === null) &&
                               newVal.every(v => typeof v !== 'object' || v === null)
          
          if (allPrimitives) {
            // Arrays de primitivos - mostrar el cambio completo
            fieldChanges.push({
              field: fieldPath,
              type: ChangeType.MODIFIED,
              oldValue: oldVal,
              newValue: newVal,
              isArray: true
            })
          } else {
            // Arrays de objetos sin IDs - comparar por posición
            const maxLength = Math.max(oldVal.length, newVal.length)
            for (let i = 0; i < maxLength; i++) {
              if (i >= oldVal.length) {
                fieldChanges.push({
                  field: `${fieldPath}[${i}]`,
                  type: ChangeType.ADDED,
                  oldValue: undefined,
                  newValue: newVal[i]
                })
              } else if (i >= newVal.length) {
                fieldChanges.push({
                  field: `${fieldPath}[${i}]`,
                  type: ChangeType.REMOVED,
                  oldValue: oldVal[i],
                  newValue: undefined
                })
              } else if (!areEqual(oldVal[i], newVal[i])) {
                if (typeof oldVal[i] === 'object' && oldVal[i] !== null &&
                    typeof newVal[i] === 'object' && newVal[i] !== null &&
                    !Array.isArray(oldVal[i]) && !Array.isArray(newVal[i])) {
                  const nestedChanges = compareObjects(oldVal[i], newVal[i], `${fieldPath}[${i}]`)
                  fieldChanges.push(...nestedChanges)
                } else {
                  fieldChanges.push({
                    field: `${fieldPath}[${i}]`,
                    type: ChangeType.MODIFIED,
                    oldValue: oldVal[i],
                    newValue: newVal[i]
                  })
                }
              }
            }
          }
        }
      }
      // Ambos son objetos (no arrays) -> comparar recursivamente
      else if (
        oldVal !== null && newVal !== null &&
        typeof oldVal === 'object' && typeof newVal === 'object' &&
        !Array.isArray(oldVal) && !Array.isArray(newVal) &&
        !areEqual(oldVal, newVal)
      ) {
        // Comparar recursivamente los objetos anidados
        const nestedChanges = compareObjects(oldVal, newVal, fieldPath)
        fieldChanges.push(...nestedChanges)
      } 
      // Valores primitivos diferentes o cambio de tipo
      else if (!areEqual(oldVal, newVal)) {
        fieldChanges.push({
          field: fieldPath,
          type: ChangeType.MODIFIED,
          oldValue: oldVal,
          newValue: newVal,
          typeChange: typeof oldVal !== typeof newVal
        })
      }
    })

    return fieldChanges
  }

  /**
   * Obtiene una descripción legible de un elemento
   */
  function getItemDescription(item) {
    if (!item || typeof item !== 'object') return String(item)
    
    // Intentar encontrar campos descriptivos
    const descProps = ['nombre', 'name', 'titulo', 'title', 'id', 'ID', 'codigo', 'code']
    for (const prop of descProps) {
      if (item[prop]) return String(item[prop])
    }
    
    return 'Item'
  }

  /**
   * Compara dos estructuras JSON completas
   * @param {Object} oldData - Datos antiguos (formato: { key: [...] })
   * @param {Object} newData - Datos nuevos (formato: { key: [...] })
   * @param {String} dataType - Tipo de dato (para descripción)
   * @returns {Array} Lista de cambios detectados
   */
  function compareJsonData(oldData, newData, dataType = 'elemento', friendlyDataType = 'elemento') {
    // Encontrar la clave principal del array
    const oldKeys = Object.keys(oldData || {})
    const newKeys = Object.keys(newData || {})
    
    // Normalmente será la primera clave (ej: "historias", "metodos", etc.)
    const mainKey = newKeys[0] || oldKeys[0]
    
    if (!mainKey) {
      return []
    }

    const oldArray = oldData?.[mainKey] || []
    const newArray = newData?.[mainKey] || []

    return compareArrays(oldArray, newArray, dataType, friendlyDataType)
  }

  /**
   * Aplica un cambio a un conjunto de datos
   */
  function applyChange(currentData, change, mainKey) {
    const newData = JSON.parse(JSON.stringify(currentData)) // Deep clone
    const array = newData[mainKey] || []

    if (change.type === ChangeType.ADDED) {
      // Añadir nuevo elemento
      array.push(change.newValue)
    } else if (change.type === ChangeType.REMOVED) {
      // Eliminar elemento
      const index = array.findIndex(item => getItemId(item) === change.id)
      if (index !== -1) {
        array.splice(index, 1)
      }
    } else if (change.type === ChangeType.MODIFIED) {
      // Modificar elemento existente
      const index = array.findIndex(item => getItemId(item) === change.id)
      if (index !== -1) {
        array[index] = change.newValue
      }
    }

    newData[mainKey] = array
    return newData
  }

  /**
   * Aplica múltiples cambios de una vez
   */
  function applyChanges(currentData, changes, mainKey) {
    let result = JSON.parse(JSON.stringify(currentData))
    
    changes.forEach(change => {
      result = applyChange(result, change, mainKey)
    })
    
    return result
  }

  return {
    ChangeType,
    compareJsonData,
    compareArrays,
    compareObjects,
    applyChange,
    applyChanges,
    getItemId,
    getItemDescription,
    getChangeDescription
  }
}
